import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskedLines, Reveal } from "./Seo";
import { LOGOS, PARENT_SITE } from "../lib/site";

/* ---------- Shared primitives ---------- */

export function Eyebrow({ children, dark = false, className = "" }) {
  return (
    <p className={`font-mono text-[11px] md:text-xs uppercase tracking-[0.25em] ${dark ? "text-lime" : "text-charcoal/70"} ${className}`}>
      <span className="inline-block h-2 w-2 bg-lime mr-3 align-middle" aria-hidden="true"></span>
      {children}
    </p>
  );
}

export function SectionHead({ dark = false, eyebrow, title, lead, className = "" }) {
  return (
    <div className={`max-w-3xl ${className}`}>
      {eyebrow ? <Reveal><Eyebrow dark={dark}>{eyebrow}</Eyebrow></Reveal> : null}
      <Reveal delay={0.08}>
        <h2 className={`font-display font-bold tracking-tight text-3xl md:text-[2.75rem] leading-[1.08] mt-5 ${dark ? "text-white" : "text-charcoal"}`}>
          {title}
        </h2>
      </Reveal>
      {lead ? (
        <Reveal delay={0.16}>
          <p className={`mt-5 text-base md:text-lg leading-relaxed ${dark ? "text-softgrey" : "text-charcoal/70"}`}>{lead}</p>
        </Reveal>
      ) : null}
    </div>
  );
}

export function StatusPill({ status, dark = true }) {
  return (
    <span
      data-testid="product-status-pill"
      className={`inline-flex items-center gap-2 border px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] ${
        dark ? "border-lime/40 text-lime bg-lime/5" : "border-charcoal/20 text-charcoal bg-surface"
      }`}
    >
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-lime" aria-hidden="true"></span>
      {status.label}
    </span>
  );
}

export function BtnPrimary({ to, href, children, testid, className = "", onClick }) {
  const cls = `inline-flex items-center justify-center gap-2 bg-lime text-charcoal font-semibold px-7 py-4 text-sm md:text-base hover:bg-limelight transition-colors duration-200 ${className}`;
  if (href)
    return (
      <a data-testid={testid} href={href} target="_blank" rel="noopener noreferrer" className={cls} onClick={onClick}>
        {children}
      </a>
    );
  return (
    <Link data-testid={testid} to={to} className={cls} onClick={onClick}>
      {children}
    </Link>
  );
}

export function BtnGhost({ to, href, children, testid, dark = true, className = "", onClick }) {
  const cls = `inline-flex items-center justify-center gap-2 border px-7 py-4 text-sm md:text-base font-semibold transition-colors duration-200 ${
    dark ? "border-white/30 text-white hover:border-lime hover:text-lime" : "border-charcoal/30 text-charcoal hover:border-charcoal"
  } ${className}`;
  if (href)
    return (
      <a data-testid={testid} href={href} target="_blank" rel="noopener noreferrer" className={cls} onClick={onClick}>
        {children}
      </a>
    );
  return (
    <Link data-testid={testid} to={to} className={cls} onClick={onClick}>
      {children}
    </Link>
  );
}

/* ---------- Logo (Softline responsive logo without background box) ---------- */

export function LogoChip({ className = "", size = "h-8 md:h-9" }) {
  return (
    <img
      src={LOGOS.nigeria}
      alt="Softline Techs & Services Nigeria"
      className={`${size} w-auto object-contain select-none transition-opacity duration-200 hover:opacity-90 ${className}`}
      data-testid="softline-nigeria-logo"
    />
  );
}

/* ---------- Editorial marquee ---------- */

export function Marquee({ items }) {
  const row = (hidden) => (
    <div className="flex shrink-0 items-center" aria-hidden={hidden || undefined}>
      {items.map((item, i) => (
        <span key={i} className="flex items-center">
          <span className="font-display font-bold text-xl md:text-2xl tracking-tight text-charcoal/85 px-8 whitespace-nowrap">{item}</span>
          <span className="h-2.5 w-2.5 rotate-45 bg-lime inline-block" aria-hidden="true"></span>
        </span>
      ))}
    </div>
  );
  return (
    <div className="marquee-wrap overflow-hidden border-y border-charcoal/10 bg-white py-5" data-testid="editorial-marquee">
      <div className="flex w-max animate-marquee">
        {row(false)}
        {row(true)}
      </div>
    </div>
  );
}

/* ---------- Interior page hero ---------- */

export function PageHero({ eyebrow, title, lead, crumbs = [], children }) {
  return (
    <section className="relative bg-charcoal noise-bg bg-grid-dark overflow-hidden" data-testid="page-hero">
      <div className="shell pt-36 pb-16 md:pt-44 md:pb-24 relative">
        <nav aria-label="Breadcrumb" className="mb-8">
          <ol className="flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-softgrey/70">
            {crumbs.map((c, i) => (
              <li key={i} className="flex items-center gap-2">
                {i > 0 && <span aria-hidden="true" className="text-lime">/</span>}
                {c.to ? (
                  <Link to={c.to} className="hover:text-lime transition-colors duration-200">
                    {c.label}
                  </Link>
                ) : (
                  <span className="text-softgrey">{c.label}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>
        {eyebrow ? <Eyebrow dark>{eyebrow}</Eyebrow> : null}
        <MaskedLines
          as="h1"
          lines={title}
          delay={0.1}
          className="font-display font-bold tracking-tight text-white text-4xl sm:text-5xl lg:text-[3.75rem] leading-[1.05] mt-6 max-w-4xl"
        />
        {lead ? <Reveal delay={0.35}><p className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-softgrey">{lead}</p></Reveal> : null}
        {children}
      </div>
    </section>
  );
}

/* ---------- Product screenshot placeholder (no fabricated UI) ---------- */

export function ShotPlaceholder({ label, tone = "dark", className = "" }) {
  return (
    <div
      data-testid="product-screenshot-placeholder"
      className={`flex flex-col items-center justify-center gap-3 border-2 border-dashed p-10 text-center ${
        tone === "dark" ? "border-white/25 bg-white/[0.03] text-softgrey" : "border-charcoal/25 bg-surface text-charcoal/60"
      } ${className}`}
    >
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <rect x="2" y="4" width="20" height="14" rx="1.5" />
        <path d="M2 8h20M6 6.5h.01M8.5 6.5h.01" />
        <path d="M8 12h8M8 15h5" />
      </svg>
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] max-w-xs">{label}</p>
      <p className="text-xs max-w-xs">Approved product screenshots will replace this placeholder before launch.</p>
    </div>
  );
}

/* ---------- Consultation CTA band ---------- */

export function CtaBand() {
  return (
    <section className="bg-ink noise-bg relative" data-testid="cta-band">
      <div className="shell py-24 md:py-32 relative">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <Eyebrow dark>Start a conversation</Eyebrow>
            <MaskedLines
              as="h2"
              lines={["Let's Build What's Next."]}
              className="font-display font-bold tracking-tight text-white text-4xl md:text-6xl leading-[1.02] mt-6"
            />
            <Reveal delay={0.25}>
              <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-softgrey">
                Whether you're modernizing enterprise infrastructure or exploring digital products, connect with our team to
                discuss your requirements.
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-4 flex md:flex-col gap-4 md:items-stretch">
            <Reveal delay={0.3} className="flex-1">
              <BtnPrimary to="/contact" testid="cta-band-consultation" className="w-full">
                Request a Consultation <ArrowUpRight size={18} />
              </BtnPrimary>
            </Reveal>
            <Reveal delay={0.4} className="flex-1">
              <BtnGhost to="/products" testid="cta-band-products" className="w-full">
                Explore Our Products
              </BtnGhost>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Group relationship section (reused: home + about) ---------- */

export function GroupSection() {
  return (
    <section className="bg-surface" data-testid="group-section">
      <div className="shell py-24 md:py-32">
        <SectionHead
          eyebrow="The Softline Group"
          title={<>Nigerian Market Focus. International Perspective.</>}
          lead="Softline Technologies and Services Nigeria Limited is a Nigerian subsidiary of Softline Technologies and Services AB, a Swedish technology company. The Nigerian operation combines group engineering standards with a direct focus on Nigerian enterprises, government institutions and financial organisations."
        />
        <div className="mt-14 grid md:grid-cols-2 gap-6">
          <Reveal>
            <a
              href={PARENT_SITE}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="group-card-sweden"
              className="group block h-full bg-white border border-charcoal/10 p-8 md:p-10 hover:border-charcoal transition-colors duration-200"
            >
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-charcoal/60">Parent company · Sweden</p>
              <h3 className="font-display font-bold text-2xl mt-4">Softline Technologies and Services AB</h3>
              <p className="mt-3 text-charcoal/70 leading-relaxed">
                The Swedish parent company — international corporate presence, consultancy and the group's technology standards.
              </p>
              <span className="mt-6 inline-flex items-center gap-2 font-semibold text-charcoal group-hover:text-lime transition-colors duration-200">
                softlinetechs.com <ArrowUpRight size={16} />
              </span>
            </a>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full bg-charcoal text-white p-8 md:p-10" data-testid="group-card-nigeria">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-lime">Nigerian subsidiary · Nigeria</p>
              <h3 className="font-display font-bold text-2xl mt-4">Softline Technologies and Services Nigeria Limited</h3>
              <p className="mt-3 text-softgrey leading-relaxed">
                Local enterprise services, proprietary digital products, partnerships and sales engagements for the Nigerian market.
              </p>
              <span className="mt-6 inline-block font-semibold text-lime">softlinetechs.com.ng</span>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
